import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";

function SmsSettingsScreen() {
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Configuration States
    const [ppurioAccount, setPpurioAccount] = useState("");
    const [ppurioApiUrl, setPpurioApiUrl] = useState("");
    const [ppurioAccessToken, setPpurioAccessToken] = useState("");
    const [ppurioAuthKey, setPpurioAuthKey] = useState("");
    const [originalAuthKey, setOriginalAuthKey] = useState(""); // Store the real key

    // Operational Settings States
    const [senderNumber, setSenderNumber] = useState("");
    const [expireMinutes, setExpireMinutes] = useState("");
    const [dailyLimit, setDailyLimit] = useState("");
    const [smsTemplate, setSmsTemplate] = useState("");

    // Test SMS States
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testPhoneNumber, setTestPhoneNumber] = useState("");
    const [testMessage, setTestMessage] = useState("");
    const [testLoading, setTestLoading] = useState(false);

    // Config Keys
    const KEYS = {
        ACCOUNT: "SMS_PPURIO_ACCOUNT",
        API_URL: "SMS_PPURIO_API_URL",
        ACCESS_TOKEN: "SMS_PPURIO_ACCESS_TOKEN",
        AUTH_KEY: "SMS_PPURIO_AUTH_KEY",
        SENDER_NUMBER: "SMS_SENDER_NUMBER",
        EXPIRE_MINUTES: "SMS_VERIFICATION_EXPIRE_MINUTES",
        DAILY_LIMIT: "SMS_DAILY_LIMIT_PER_USER",
        SMS_TEMPLATE: "SMS_VERIFICATION_TEMPLATE",
    };

    const maskAuthKey = (key: string) => {
        if (!key) return "";
        if (key.length <= 20) return key;
        const visibleStart = 10;
        const visibleEnd = 10;
        const hiddenCount = key.length - (visibleStart + visibleEnd);
        return `${key.substring(0, visibleStart)}${'*'.repeat(hiddenCount)}${key.substring(key.length - visibleEnd)}`;
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const [accountRes, apiRes, tokenRes, keyRes, senderRes, expireRes, limitRes, templateRes] = await Promise.all([
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.ACCOUNT } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.API_URL } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.ACCESS_TOKEN } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.AUTH_KEY } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.SENDER_NUMBER } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.EXPIRE_MINUTES } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.DAILY_LIMIT } }),
                request("SystemConfig.GetConfig", { paramz: { Key: KEYS.SMS_TEMPLATE } }),
            ]);

            if (accountRes && accountRes.data) setPpurioAccount(accountRes.data.value || "");
            if (apiRes && apiRes.data) setPpurioApiUrl(apiRes.data.value || "");
            if (tokenRes && tokenRes.data) setPpurioAccessToken(tokenRes.data.value || "");

            if (keyRes && keyRes.data) {
                const realKey = keyRes.data.value || "";
                setOriginalAuthKey(realKey);
                setPpurioAuthKey(maskAuthKey(realKey));
            }

            if (senderRes && senderRes.data) setSenderNumber(senderRes.data.value || "");
            if (expireRes && expireRes.data) setExpireMinutes(expireRes.data.value || "");
            if (limitRes && limitRes.data) setDailyLimit(limitRes.data.value || "");
            if (templateRes && templateRes.data) setSmsTemplate(templateRes.data.value || "");
        } catch (error) {
            console.error("Failed to fetch SMS settings", error);
            setErrorMsg("설정을 불러오는데 실패했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const theme = useTheme();

    const handleSave = async () => {
        if (!window.confirm("설정을 저장하시겠습니까?")) {
            return;
        }

        setLoading(true);
        setSuccessMsg(null);
        setErrorMsg(null);

        try {
            // Check if Auth Key is modified (not equal to masked original)
            const authKeyToSend = (ppurioAuthKey === maskAuthKey(originalAuthKey)) ? originalAuthKey : ppurioAuthKey;

            // Save each config sequentially or in parallel
            await Promise.all([
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.ACCOUNT, Value: ppurioAccount, Description: "뿌리오 계정 ID" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.API_URL, Value: ppurioApiUrl, Description: "뿌리오 API 엔드포인트" } }),
                // request("SystemConfig.SetConfig", { paramz: { Key: KEYS.ACCESS_TOKEN, Value: ppurioAccessToken, Description: "뿌리오 API 엑세스 토큰 (보안 주의)" } }), // Token is read-only
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.AUTH_KEY, Value: authKeyToSend, Description: "뿌리오 API 인증 키 (보안 주의)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.SENDER_NUMBER, Value: senderNumber, Description: "SMS 발신 번호" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.EXPIRE_MINUTES, Value: expireMinutes, Description: "SMS 인증번호 유효 시간 (분)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.DAILY_LIMIT, Value: dailyLimit, Description: "일일 사용자 SMS 발송 제한 (회)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.SMS_TEMPLATE, Value: smsTemplate, Description: "SMS 인증 템플릿 (위치 표시자 {0} 포함)" } }),
            ]);

            setSuccessMsg("설정이 성공적으로 저장되었습니다.");
            setTimeout(() => setSuccessMsg(null), 3000);
        } catch (error) {
            console.error("Failed to save SMS settings", error);
            setErrorMsg("설정 저장 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const openTestPopup = () => {
        setTestPhoneNumber("");
        setTestMessage("테스트 메시지입니다.");
        setTestDialogOpen(true);
    };

    const closeTestPopup = () => {
        setTestDialogOpen(false);
    };

    const handleSendTestSms = async () => {
        if (!testPhoneNumber || !testMessage) {
            alert("전화번호와 메시지를 입력해주세요.");
            return;
        }

        setTestLoading(true);
        try {
            await request("Sms.SendTest", {
                paramz: {
                    PhoneNumber: testPhoneNumber,
                    Message: testMessage
                }
            });
            alert("테스트 문자가 성공적으로 발송되었습니다.");
            closeTestPopup();
        } catch (error: any) {
            console.error("Failed to send test SMS", error);
            alert(`발송 실패: ${error.message || "알 수 없는 오류"}`);
        } finally {
            setTestLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">
                                    연동 계정 설정 (뿌리오)
                                </MDTypography>
                                <MDButton variant="outlined" color="white" size="small" onClick={openTestPopup}>
                                    SMS 발송 테스트
                                </MDButton>
                            </MDBox>
                            <MDBox p={3}>
                                {successMsg && (
                                    <MDBox mb={2}>
                                        <MDAlert color="success">{successMsg}</MDAlert>
                                    </MDBox>
                                )}
                                {errorMsg && (
                                    <MDBox mb={2}>
                                        <MDAlert color="error">{errorMsg}</MDAlert>
                                    </MDBox>
                                )}
                                <MDBox mb={2}>
                                    <MDTypography variant="body2" color="text">
                                        SMS 발송 서비스(Ppurio) 연동 및 운영 정책을 설정합니다.
                                        <br />
                                        자세한 내용은{" "}
                                        <Link href="https://www.ppurio.com" target="_blank" rel="noreferrer" sx={{ fontWeight: "bold" }}>
                                            https://www.ppurio.com
                                        </Link>
                                        을 참조하세요.
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <MDBox p={2}>
                                {/* Operational Settings Section */}
                                <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                                    운영 정책 설정
                                </MDTypography>
                                <MDBox mb={1}>
                                    <MDTypography variant="button" color="text" fontWeight="regular">
                                        발신 번호 및 발송 제한 정책을 설정합니다.
                                    </MDTypography>
                                </MDBox>
                                <Grid container spacing={3}>

                                    <Grid item xs={12} md={4}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="인증번호 유효 시간 (분)"
                                                type="number"
                                                fullWidth
                                                placeholder="예: 3"
                                                value={expireMinutes}
                                                onChange={(e: any) => setExpireMinutes(e.target.value)}
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="일일 사용자 발송 제한 (회)"
                                                type="number"
                                                fullWidth
                                                placeholder="예: 5"
                                                value={dailyLimit}
                                                onChange={(e: any) => setDailyLimit(e.target.value)}
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="인증 문자 템플릿"
                                                fullWidth
                                                placeholder="[메디인스텝] 인증번호는 [{code}] 입니다."
                                                value={smsTemplate}
                                                onChange={(e: any) => setSmsTemplate(e.target.value)}
                                                helperText="인증번호가 들어갈 위치에 {code}를 입력해주세요. (예: [서비스명] 인증번호는 [{code}] 입니다.)"
                                            />
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 3 }} />

                                {/* Connection Settings Section */}
                                <MDTypography variant="h6" fontWeight="medium">
                                    연동 계정 설정 (뿌리오)
                                </MDTypography>
                                <MDBox mb={2}>
                                    <MDTypography variant="caption" color="text" fontWeight="regular">
                                        발송을 위한 정보 계정 API AuthKey를 설정하세요
                                    </MDTypography>
                                </MDBox>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="계정 (Account)"
                                                fullWidth
                                                value={ppurioAccount}
                                                onChange={(e: any) => setPpurioAccount(e.target.value)}
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="API URL"
                                                fullWidth
                                                value={ppurioApiUrl}
                                                onChange={(e: any) => setPpurioApiUrl(e.target.value)}
                                            />
                                        </MDBox>
                                    </Grid>
                                    {/* Sender Number (Moved Here) */}
                                    <Grid item xs={12}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="발신 번호 (Sender Number)"
                                                fullWidth
                                                placeholder="예: 01012345678"
                                                value={senderNumber}
                                                onChange={(e: any) => setSenderNumber(e.target.value)}
                                                helperText="발신번호는 뿌리오 발신번호로 등록된 번호를 사용해야 합니다. 새로운 번호를 등록하려면 뿌리오에 먼저 발신번호로 등록 되어 있는지 확인해주세요."
                                            />
                                        </MDBox>
                                    </Grid>
                                    {/* Auth Key (Moved UP) */}
                                    <Grid item xs={12}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="Auth KEY"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={ppurioAuthKey}
                                                onChange={(e: any) => setPpurioAuthKey(e.target.value)}
                                            />
                                        </MDBox>
                                    </Grid>
                                    {/* Current Token (Moved DOWN) */}
                                    <Grid item xs={12}>
                                        <MDBox mb={2}>
                                            <MDTypography variant="caption" color="text" fontWeight="bold">
                                                Current Token (Access Token)
                                            </MDTypography>
                                            <MDBox
                                                p={2}
                                                mt={1}
                                                borderRadius="md"
                                                style={{
                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : theme.palette.grey[200],
                                                    border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : theme.palette.grey[300]}`,
                                                    color: darkMode ? "#ffffff" : theme.palette.text.primary,
                                                    wordBreak: "break-all",
                                                    fontFamily: "monospace"
                                                }}
                                            >
                                                <MDTypography variant="body2" color="text">
                                                    {ppurioAccessToken || "토큰이 없습니다."}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <Divider />

                                <MDBox display="flex" justifyContent="flex-end" mt={2} mb={1}>
                                    <MDButton
                                        variant="gradient"
                                        color="info"
                                        onClick={handleSave}
                                        disabled={loading}
                                    >
                                        {loading ? "저장 중..." : "설정 저장"}
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Test SMS Dialog */}
            <Dialog open={testDialogOpen} onClose={closeTestPopup} maxWidth="sm" fullWidth>
                <DialogTitle>SMS 발송 테스트</DialogTitle>
                <DialogContent>
                    <MDBox component="form" role="form" mt={2}>
                        <MDBox mb={2}>
                            <MDInput
                                label="수신 전화번호"
                                fullWidth
                                placeholder="예: 01012345678"
                                value={testPhoneNumber}
                                onChange={(e: any) => setTestPhoneNumber(e.target.value)}
                            />
                        </MDBox>
                        <MDBox mb={2}>
                            <MDInput
                                label="메시지 내용"
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="테스트 메시지를 입력하세요"
                                value={testMessage}
                                onChange={(e: any) => setTestMessage(e.target.value)}
                            />
                        </MDBox>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={closeTestPopup} color="secondary">
                        취소
                    </MDButton>
                    <MDButton onClick={handleSendTestSms} color="info" disabled={testLoading}>
                        {testLoading ? "발송 중..." : "발송"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default SmsSettingsScreen;
